import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { getSavedChats, getSavedChat, saveChat, deleteSavedChat } from '../services/apiService';
import { 
  getAccessToken, 
  getSavedChatsList, 
  setSavedChatsList,
  getSavedChatsAreaTop,
  setSavedChatsAreaTop,
  getSavedChatsNeedsInit,
  removeSavedChatsNeedsInit,
  getCurrentSubject
} from '../services/sessionStorageService';
import logger from '../utils/logger';

const Sidebar = ({ isLoggedIn, onLogout, onLoadSavedChat }) => {
  const location = useLocation();
  const [savedChats, setSavedChats] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saveDialogName, setSaveDialogName] = useState('');
  const [saveDialogError, setSaveDialogError] = useState('');
  const [isLoadingSavedChats, setIsLoadingSavedChats] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [isResizing, setIsResizing] = useState(false);
  const [savedAreaTop, setSavedAreaTop] = useState(null);
  const [isDraggingSeparator, setIsDraggingSeparator] = useState(false);
  
  const sidebarRef = useRef(null);
  const navRef = useRef(null);
  const askJuniorRef = useRef(null);
  const savedAreaTopRef = useRef(null);
  const defaultTopRef = useRef(null);
  const accessToken = getAccessToken();
  const isOnChatbotPage = location.pathname === '/chatbot';

  // Initialize sidebar width from sessionStorage
  useEffect(() => {
    const savedWidth = sessionStorage.getItem('sidebar_width');
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (width >= 200 && width <= 600) { // Min/max constraints
        setSidebarWidth(width);
      }
    }
  }, []);

  // Save sidebar width to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('sidebar_width', sidebarWidth.toString());
  }, [sidebarWidth]);

  // ===== Saved Chats Area Positioning (RSCAF2) =====
  useLayoutEffect(() => {
    if (!isLoggedIn || !isOnChatbotPage) return;
    const sidebarEl = sidebarRef.current;
    const navEl = navRef.current;
    const askEl = askJuniorRef.current;
    if (!sidebarEl || !navEl || !askEl) return;

    const storedTop = getSavedChatsAreaTop();

    // Compute limits based on DOM
    const sidebarRect = sidebarEl.getBoundingClientRect();
    const navRect = navEl.getBoundingClientRect();
    const askRect = askEl.getBoundingClientRect();
    const topLimit = Math.round(askRect.bottom - sidebarRect.top); // cannot go above bottom of Ask Junior
    const defaultTop = Math.round(navRect.bottom - sidebarRect.top + 8); // lower drag limit
    const bottomLimit = defaultTop;
    const needsInit = getSavedChatsNeedsInit();

    const normalize = (value) => Math.max(topLimit, Math.min(value, bottomLimit));

    if (needsInit) {
      // Defer measurement until layout stabilizes (double rAF)
      const deferredMeasure = () => {
        const sidebarNow = sidebarRef.current;
        const askNow = askJuniorRef.current;
        const navNow = navRef.current;
        if (!sidebarNow || !askNow || !navNow) return;
        const sidebarNowRect = sidebarNow.getBoundingClientRect();
        const askNowRect = askNow.getBoundingClientRect();
        const navNowRect = navNow.getBoundingClientRect();
        const topLimitNow = Math.round(askNowRect.bottom - sidebarNowRect.top);
        const defaultTopNow = Math.round(navNowRect.bottom - sidebarNowRect.top + 8);
        const bottomLimitNow = defaultTopNow; // use lower drag limit as bottom limit
        // On first real init, set to the LOWER DRAG LIMIT (clamped to top limit)
        const normalized = Math.max(topLimitNow, Math.min(defaultTopNow, bottomLimitNow));
        setSavedAreaTop(normalized);
        savedAreaTopRef.current = normalized;
        defaultTopRef.current = defaultTopNow;
        setSavedChatsAreaTop(normalized);
        removeSavedChatsNeedsInit();
      };
      requestAnimationFrame(() => requestAnimationFrame(deferredMeasure));
    } else {
      if (Number.isFinite(storedTop)) {
        const normalized = normalize(storedTop);
        setSavedAreaTop(normalized);
        savedAreaTopRef.current = normalized;
        defaultTopRef.current = bottomLimit;
        if (normalized !== storedTop) setSavedChatsAreaTop(normalized);
      } else {
        // Fallback if somehow missing: use bottomLimit
        setSavedAreaTop(bottomLimit);
        savedAreaTopRef.current = bottomLimit;
        defaultTopRef.current = bottomLimit;
        setSavedChatsAreaTop(bottomLimit);
      }
    }
  }, [isLoggedIn, isOnChatbotPage]);

  // Recompute constraints on window resize
  useEffect(() => {
    if (!isLoggedIn || !isOnChatbotPage) return;
    const onResize = () => {
      const sidebarEl = sidebarRef.current;
      const askEl = askJuniorRef.current;
      const navEl = navRef.current;
      if (!sidebarEl || !askEl || !navEl) return;
      const sidebarRect = sidebarEl.getBoundingClientRect();
      const askRect = askEl.getBoundingClientRect();
      const navRect = navEl.getBoundingClientRect();
      const topLimit = Math.round(askRect.bottom - sidebarRect.top);
      const bottomLimit = Math.round(navRect.bottom - sidebarRect.top + 8); // lower drag limit
      const current = savedAreaTopRef.current ?? bottomLimit;
      const clamped = Math.max(topLimit, Math.min(current, bottomLimit));
      setSavedAreaTop(clamped);
      savedAreaTopRef.current = clamped;
      defaultTopRef.current = bottomLimit;
      setSavedChatsAreaTop(clamped);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isLoggedIn, isOnChatbotPage]);

  const loadSavedChats = useCallback(async () => {
    try {
      setIsLoadingSavedChats(true);
      logger.info('Loading saved chats from API');
      
      const response = await getSavedChats(accessToken);
      if (response.success && response.data) {
        setSavedChats(response.data);
        setSavedChatsList(response.data);
        logger.info('Saved chats loaded successfully', { count: response.data.length });
      } else {
        logger.error('Failed to load saved chats', { error: response.error });
        // Try to load from session storage as fallback
        const cachedChats = getSavedChatsList();
        setSavedChats(cachedChats);
      }
    } catch (error) {
      logger.error('Error loading saved chats', { error: error.message });
      // Load from session storage as fallback
      const cachedChats = getSavedChatsList();
      setSavedChats(cachedChats);
    } finally {
      setIsLoadingSavedChats(false);
    }
  }, [accessToken]);

  // Load saved chats when user is logged in and on chatbot page
  useEffect(() => {
    if (isLoggedIn && isOnChatbotPage && accessToken) {
      loadSavedChats();
    }
  }, [isLoggedIn, isOnChatbotPage, accessToken, loadSavedChats]);

  const handleSaveChat = () => {
    setShowSaveDialog(true);
    setSaveDialogName('');
    setSaveDialogError('');
  };

  const handleSaveDialogSubmit = async () => {
    const trimmedName = saveDialogName.trim();
    
    // Validation
    if (!trimmedName) {
      setSaveDialogError('Please enter a title for your chat');
      return;
    }
    if (trimmedName.length < 3) {
      setSaveDialogError('Title must be at least 3 characters long');
      return;
    }
    if (trimmedName.length > 100) {
      setSaveDialogError('Title cannot exceed 100 characters');
      return;
    }

    try {
      // Get current chat history from session storage
      const chatHistory = JSON.parse(sessionStorage.getItem('chatHistory') || '[]');
      
      if (chatHistory.length === 0) {
        setSaveDialogError('No chat history to save');
        return;
      }

      // Get current subject from sessionStorage
      const currentSubject = getCurrentSubject();
      
      logger.info('Saving chat', { 
        chatName: trimmedName, 
        messageCount: chatHistory.length,
        subject: currentSubject 
      });
      
      // Pass current subject to saveChat
      const response = await saveChat(trimmedName, chatHistory, accessToken, currentSubject);
      if (response.success) {
        logger.info('Chat saved successfully', { 
          chatId: response.data.chat_id,
          subject: currentSubject 
        });
        
        // Refresh saved chats list
        await loadSavedChats();
        
        // Close dialog
        setShowSaveDialog(false);
        setSaveDialogName('');
        setSaveDialogError('');
      } else {
        setSaveDialogError(response.error || 'Failed to save chat');
      }
    } catch (error) {
      logger.error('Error saving chat', { error: error.message });
      setSaveDialogError('Unable to save chat - please try again');
    }
  };

  const handleSaveDialogCancel = () => {
    setShowSaveDialog(false);
    setSaveDialogName('');
    setSaveDialogError('');
  };

  const handleSaveDialogKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveDialogSubmit();
    } else if (e.key === 'Escape') {
      handleSaveDialogCancel();
    }
  };

  const handleLoadSavedChat = async (chatId) => {
    try {
      logger.info('Loading saved chat', { chatId });
      
      const response = await getSavedChat(chatId, accessToken);
      if (response.success && response.data) {
        logger.info('Saved chat loaded successfully', { chatName: response.data.chat_name });
        
        // Pass the chat data to parent component
        if (onLoadSavedChat) {
          onLoadSavedChat(response.data);
        }
      } else {
        logger.error('Failed to load saved chat', { chatId, error: response.error });
      }
    } catch (error) {
      logger.error('Error loading saved chat', { chatId, error: error.message });
    }
  };

  const handleDeleteSavedChat = (chatId, chatName) => {
    setShowDeleteConfirm({ chatId, chatName });
  };

  const confirmDeleteSavedChat = async () => {
    if (!showDeleteConfirm) return;
    
    const { chatId, chatName } = showDeleteConfirm;
    
    try {
      logger.info('Deleting saved chat', { chatId, chatName });
      
      const response = await deleteSavedChat(chatId, accessToken);
      if (response.success) {
        logger.info('Saved chat deleted successfully', { chatId });
        
        // Refresh saved chats list
        await loadSavedChats();
        
        setShowDeleteConfirm(null);
      } else {
        logger.error('Failed to delete saved chat', { chatId, error: response.error });
      }
    } catch (error) {
      logger.error('Error deleting saved chat', { chatId, error: error.message });
    }
  };

  const cancelDeleteSavedChat = () => {
    setShowDeleteConfirm(null);
  };

  // Resize functionality
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + deltaX)); // Constrain between 200px and 600px
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Separator drag (vertical) for Saved Chats Area
  const onSeparatorMouseDown = (e) => {
    if (!isLoggedIn || !isOnChatbotPage) return;
    e.preventDefault();
    const sidebarEl = sidebarRef.current;
    const navEl = navRef.current;
    const askEl = askJuniorRef.current;
    if (!sidebarEl || !navEl || !askEl) return;

    setIsDraggingSeparator(true);

    const sidebarRect = sidebarEl.getBoundingClientRect();
    const askRect = askEl.getBoundingClientRect();
    const navRect = navEl.getBoundingClientRect();

    const topLimit = Math.round(askRect.bottom - sidebarRect.top);
    const defaultTop = Math.round(navRect.bottom - sidebarRect.top + 8);
    const startY = e.clientY;
    const startTop = savedAreaTop ?? defaultTop;

    const onMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      let nextTop = startTop + deltaY;
      // Clamp: cannot move above Ask Junior bottom; cannot move below default position
      nextTop = Math.max(topLimit, Math.min(nextTop, defaultTop));
      setSavedAreaTop(nextTop);
      savedAreaTopRef.current = nextTop;
    };

    const onMouseUp = () => {
      setIsDraggingSeparator(false);
      // Persist final value
      if (savedAreaTopRef.current !== null && savedAreaTopRef.current !== undefined) {
        setSavedChatsAreaTop(savedAreaTopRef.current);
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onSeparatorTouchStart = (e) => {
    if (!isLoggedIn || !isOnChatbotPage) return;
    if (!e.touches || e.touches.length === 0) return;
    const touch = e.touches[0];
    const sidebarEl = sidebarRef.current;
    const navEl = navRef.current;
    const askEl = askJuniorRef.current;
    if (!sidebarEl || !navEl || !askEl) return;

    setIsDraggingSeparator(true);

    const sidebarRect = sidebarEl.getBoundingClientRect();
    const askRect = askEl.getBoundingClientRect();
    const navRect = navEl.getBoundingClientRect();

    const topLimit = Math.round(askRect.bottom - sidebarRect.top);
    const defaultTop = Math.round(navRect.bottom - sidebarRect.top + 8);
    const startY = touch.clientY;
    const startTop = savedAreaTop ?? defaultTop;

    const onTouchMove = (moveEvent) => {
      if (!moveEvent.touches || moveEvent.touches.length === 0) return;
      const t = moveEvent.touches[0];
      const deltaY = t.clientY - startY;
      let nextTop = startTop + deltaY;
      nextTop = Math.max(topLimit, Math.min(nextTop, defaultTop));
      setSavedAreaTop(nextTop);
      savedAreaTopRef.current = nextTop;
    };

    const onTouchEnd = () => {
      setIsDraggingSeparator(false);
      if (savedAreaTopRef.current !== null && savedAreaTopRef.current !== undefined) {
        setSavedChatsAreaTop(savedAreaTopRef.current);
      }
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  return (
    <div 
      ref={sidebarRef}
      className={`sidebar ${isResizing ? 'resizing' : ''}`} 
      data-testid="sidebar"
      style={{ width: `${sidebarWidth}px` }}
    >
      <div className="sidebar-header">
        <img 
          src="/images/jj_logo_nav_sidebar.png?v=1" 
          alt="Jeeves Junior" 
          className="logo"
        />
      </div>
      
      <nav className="sidebar-nav" ref={navRef}>
        <Link 
          to="/" 
          className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
        >
          <img src="/images/jj_icon_home.png" alt="Home" className="nav-icon" />
          <span>HOME</span>
        </Link>
        
        {!isLoggedIn ? (
          <Link 
            to="/login" 
            className={`nav-item ${location.pathname === '/login' ? 'active' : ''}`}
          >
            <img src="/images/jj_icon_login.png" alt="Log In" className="nav-icon" />
            <span>LOG IN</span>
          </Link>
        ) : (
          <>
            <Link 
              to="/chatbot" 
              className={`nav-item ${location.pathname === '/chatbot' ? 'active' : ''}`}
              ref={askJuniorRef}
            >
              <img src="/images/jj_icon_chatbot.png" alt="Ask Junior" className="nav-icon" />
              <span>ASK JUNIOR</span>
            </Link>
            
            <Link 
              to="/about" 
              className={`nav-item ${location.pathname === '/about' ? 'active' : ''}`}
            >
              <img src="/images/jj_icon_about.svg" alt="About" className="nav-icon" />
              <span>ABOUT</span>
            </Link>
            
            <Link 
              to="/profile" 
              className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
            >
              <img src="/images/jj_icon_person.svg" alt="Profile" className="nav-icon" />
              <span>PROFILE</span>
            </Link>
            
            <button onClick={onLogout} className="nav-item logout-button">
              <img src="/images/jj_icon_logout.png" alt="Log Out" className="nav-icon" />
              <span>LOG OUT</span>
            </button>
          </>
        )}
      </nav>
      
      {/* Saved Chats Area (absolute, resizable vertically) */}
      {isLoggedIn && isOnChatbotPage && savedAreaTop !== null && (
        <div 
          className={`saved-chats-wrapper ${isDraggingSeparator ? 'dragging' : ''}`}
          style={{ top: `${savedAreaTop}px` }}
        >
          <div 
            className="saved-chats-separator"
            onMouseDown={onSeparatorMouseDown}
            onTouchStart={onSeparatorTouchStart}
            title="Drag to resize Saved Chats area"
          >
            <div className="separator-grip" />
          </div>

          <button onClick={handleSaveChat} className="save-chat-button">
            <span>💾</span>
            <span>SAVE CHAT</span>
          </button>

          <div className="saved-chats-section">
            <div className="saved-chats-header">
              <span>Saved Chats</span>
            </div>
            
            <div className="saved-chats-list">
              {isLoadingSavedChats ? (
                <div className="saved-chats-loading">Loading...</div>
              ) : savedChats.length === 0 ? (
                <div className="saved-chats-empty">No saved chats</div>
              ) : (
                savedChats.map((chat) => (
                  <div key={chat.chat_id} className="saved-chat-item">
                    <div 
                      className="saved-chat-name"
                      onClick={() => handleLoadSavedChat(chat.chat_id)}
                      title={chat.chat_name}
                    >
                      {chat.chat_name}
                    </div>
                    <button
                      className="saved-chat-delete"
                      onClick={() => handleDeleteSavedChat(chat.chat_id, chat.chat_name)}
                      title="Delete chat"
                    >
                      🗑️
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Chat Dialog */}
      {showSaveDialog && (
        <div className="modal-overlay">
          <div className="modal-dialog save-chat-dialog">
            <div className="modal-header">
              <h3>Save Chat</h3>
            </div>
            <div className="modal-body">
              <input
                type="text"
                value={saveDialogName}
                onChange={(e) => setSaveDialogName(e.target.value)}
                onKeyDown={handleSaveDialogKeyPress}
                placeholder="Enter a title for this chat"
                className={`chat-name-input ${saveDialogError ? 'error' : ''}`}
                maxLength={100}
                autoFocus
              />
              {saveDialogError && (
                <div className="error-message">{saveDialogError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleSaveDialogCancel} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleSaveDialogSubmit} className="btn-save">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-dialog delete-confirm-dialog">
            <div className="modal-header">
              <h3>Jeeves Junior Asks</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the chat "{showDeleteConfirm.chatName}"?</p>
            </div>
            <div className="modal-footer">
              <button onClick={cancelDeleteSavedChat} className="btn-cancel">
                Cancel
              </button>
              <button onClick={confirmDeleteSavedChat} className="btn-ok">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Resize Handle */}
      <div 
        className="sidebar-resize-handle"
        onMouseDown={handleMouseDown}
        title="Drag to resize sidebar"
      />
    </div>
  );
};

export default Sidebar; 